import { useMemo } from 'react';
import { Registry } from '@/graphql/__generated__/onboarding';
import { Registry as LegalRegistry } from '@/graphql/__generated__/onboarding-legal';
import { normalizeAntifraudData, NormalizedAntifraudData } from '@/utils/antifraudDataNormalizer';

export interface AntifraudData {
	trully: NormalizedAntifraudData | null;
	unico: NormalizedAntifraudData | null;
}

export const useNormalizedAntifraudData = (
	registry: Registry | LegalRegistry | null | undefined
): NormalizedAntifraudData | null => {
	return useMemo(() => {
		if (!registry) {
			return null;
		}

		if ('legalRepresentative' in registry) {
			const legalRegistry = registry as LegalRegistry;
			const antifraudAnalysis = legalRegistry?.legalRepresentative?.antifraudAnalysis;
			if (!antifraudAnalysis) {
				return null;
			}
			return normalizeAntifraudData(antifraudAnalysis);
		}

		const onboardingRegistry = registry as Registry;
		if (onboardingRegistry.unicoAntifraudAnalysis) {
			return normalizeAntifraudData(onboardingRegistry.unicoAntifraudAnalysis);
		}

		if (onboardingRegistry.antifraudAnalysis) {
			return normalizeAntifraudData(onboardingRegistry.antifraudAnalysis);
		}

		return null;
	}, [registry]);
};

export const useSeparatedAntifraudData = (
	registry: Registry | LegalRegistry | null | undefined
): AntifraudData => {
	return useMemo(() => {
		if (!registry) {
			return { trully: null, unico: null };
		}

		if ('legalRepresentative' in registry) {
			const legalRegistry = registry as LegalRegistry;
			const antifraudAnalysis = legalRegistry?.legalRepresentative?.antifraudAnalysis;
			if (!antifraudAnalysis) {
				return { trully: null, unico: null };
			}
			const normalized = normalizeAntifraudData(antifraudAnalysis);
			if (normalized?.isUnico) {
				return { trully: null, unico: normalized };
			}
			return { trully: normalized, unico: null };
		}

		const onboardingRegistry = registry as Registry;
		const trullyData = onboardingRegistry.antifraudAnalysis
			? normalizeAntifraudData(onboardingRegistry.antifraudAnalysis)
			: null;
		const unicoData = onboardingRegistry.unicoAntifraudAnalysis
			? normalizeAntifraudData(onboardingRegistry.unicoAntifraudAnalysis)
			: null;

		return {
			trully: trullyData?.isUnico ? null : trullyData,
			unico: unicoData?.isUnico ? unicoData : null
		};
	}, [registry]);
};
